import React, { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { Duration, Easing, Spacing } from '@common/tokens';
import { LoadingIcon } from './_loading-icon';

export type ButtonSizeType = 'sm' | 'md' | 'lg';

const ICON_GAP_BY_SIZE: Record<ButtonSizeType, string> = {
  sm: `${Spacing.xs}px`,
  md: `${Spacing.sm}px`,
  lg: `${Spacing.sm}px`,
};

export function ButtonContent({
  icon,
  iconRight,
  iconOnly,
  loading,
  size,
  children,
}: {
  icon?: React.ReactNode;
  iconRight?: React.ReactNode;
  iconOnly: boolean;
  loading: boolean;
  size: ButtonSizeType;
  children?: React.ReactNode;
}) {
  const leftIconNode = icon && loading ? <LoadingIcon size={size} /> : icon;

  const {
    mounted: spinnerMounted,
    visible: spinnerVisible,
    handleTransitionEnd,
  } = useAnimatedPresence(loading);

  const spinnerClasses = clsx(
    'wk-button__spinner',
    spinnerVisible && 'wk-button__spinner--visible'
  );

  return (
    <>
      {icon && <span className="wk-button__icon">{leftIconNode}</span>}
      {!icon && !iconOnly && spinnerMounted && (
        <span className={spinnerClasses} onTransitionEnd={handleTransitionEnd}>
          <LoadingIcon size={size} />
        </span>
      )}
      {children && <span className="wk-button__content">{children}</span>}
      {iconRight && <span className="wk-button__icon">{iconRight}</span>}
      <style jsx>{`
        .wk-button__icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .wk-button__spinner {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          max-width: 0;
          opacity: 0;
          margin-right: 0;
          overflow: hidden;
          transition:
            max-width ${Duration.normal} ${Easing.ease},
            opacity ${Duration.normal} ${Easing.ease},
            margin-right ${Duration.normal} ${Easing.ease};
        }
        .wk-button__spinner--visible {
          max-width: ${Spacing.lg}px;
          opacity: 1;
          margin-right: ${ICON_GAP_BY_SIZE[size]};
        }

        .wk-button__content {
          display: inline-flex;
          align-items: center;
        }

        .wk-button__icon + .wk-button__content,
        .wk-button__content + .wk-button__icon {
          margin-left: ${ICON_GAP_BY_SIZE[size]};
        }
      `}</style>
    </>
  );
}

function useAnimatedPresence(active: boolean) {
  const [mounted, setMounted] = useState(active);
  const [visible, setVisible] = useState(active);
  const frameRef = useRef<number | null>(null);

  useEffect(() => {
    if (active) {
      setMounted(true);
      frameRef.current = requestAnimationFrame(() => {
        frameRef.current = requestAnimationFrame(() => setVisible(true));
      });
    } else {
      setVisible(false);
    }

    return () => {
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
    };
  }, [active]);

  const handleTransitionEnd = () => {
    if (!active) setMounted(false);
  };

  return { mounted, visible, handleTransitionEnd };
}
